"use client"
import type { FragmentOf } from "@opensea/graphql"
import { readFragment } from "@opensea/graphql"
import {
  BottomSheetBody,
  BottomSheetFooter,
  BottomSheetHeader,
  BottomSheetHeaderTitle,
  Flex,
  Form,
  Media,
  Separator,
  SkeletonBlock,
  tv,
} from "@opensea/ui-kit"
import { useLocalStorage } from "@opensea/ui-kit/hooks"
import { useEffect, useMemo } from "react"
import {
  Controller,
  useFormContext,
  useFormState,
  useWatch,
} from "react-hook-form"
import { useTranslations } from "use-intl"
import { Button } from "../../../../../components/Button"
import {
  CreateOrdersSummary,
  CreateOrdersSummarySkeleton,
} from "../../../../../components/CreateOrdersSummary"
import { ExpirationSelect } from "../../../../../components/ExpirationSelect"
import { useIsFormSubmitDisabled } from "../../../../../lib/form/valid"
import {
  DEFAULT_EXPIRATION,
  LISTING_EXPIRATION_CUSTOM_EXPIRATION_LOCAL_STORAGE,
  LISTING_EXPIRATION_LOCAL_STORAGE,
} from "../../../constants"
import type { ListingFlowFormValues } from "../../types"
import {
  ListingFlowHeaderActions,
  ListingFlowHeaderActionsSkeleton,
} from "../ListingFlowHeaderActions"
import {
  ListingFlowItemsTable,
  ListingFlowItemsTableSkeleton,
} from "../ListingFlowItemsTable/ListingFlowItemsTable"
import { ListingFlowForm_itemFragment } from "./ListingFlowForm.fragment"

type ListingFlowFormProps = {
  items: FragmentOf<typeof ListingFlowForm_itemFragment>[]
  onSubmit: (values: ListingFlowFormValues) => void
}

const footerVariants = tv({
  slots: {
    buttonContainer:
      "grid grid-cols-2 gap-3 pb-4 md:flex md:justify-end md:pb-0",
  },
})

export const ListingFlowForm = ({
  items: itemKeys,
  onSubmit,
}: ListingFlowFormProps) => {
  const t = useTranslations("wallet.ListingFlowForm")
  const items = itemKeys.map(itemKey => ({
    ...readFragment(ListingFlowForm_itemFragment, itemKey),
  }))

  const { control, handleSubmit, setValue } =
    useFormContext<ListingFlowFormValues>()
  const { errors } = useFormState({ control })
  const listings = useWatch({ control, name: "listings" })
  const adjustForFees = useWatch({ control, name: "adjustForFees" })
  const creatorFeeEnforced = listings.some(
    listing => listing.isCreatorFeeRequired,
  )
  const [_, setListingExpiration] = useLocalStorage(
    LISTING_EXPIRATION_LOCAL_STORAGE,
    DEFAULT_EXPIRATION,
  )
  const [__, setListingExpirationCustom] = useLocalStorage<string | undefined>(
    LISTING_EXPIRATION_CUSTOM_EXPIRATION_LOCAL_STORAGE,
    undefined,
  )

  const tableItems = listings.flatMap(listing => {
    const matchedItem = items.find(item => item.id === listing.id)

    return matchedItem ? [matchedItem] : []
  })

  const isEditListing = tableItems.every(
    item => item.lowestListingForOwner && !item.isFungible,
  )

  const globalApplyCreatorFee = useWatch({ control, name: "applyCreatorFee" })
  useEffect(() => {
    listings.forEach((_, index) => {
      setValue(`listings.${index}.applyCreatorFee`, globalApplyCreatorFee)
    })
  }, [globalApplyCreatorFee, listings.length, setValue])

  const isPrivateListing = Boolean(
    useWatch({ control, name: "reservedBuyer.resolvedAddress" }),
  )
  useEffect(() => {
    listings.forEach((_, index) => {
      setValue(
        `listings.${index}.openSeaFeeBasisPoints`,
        isPrivateListing
          ? 0
          : items[index].collection?.fees.openseaFee?.feeBasisPoints || 0,
      )
    })
  }, [isPrivateListing, listings.length, setValue])

  const isFormSubmitDisabled = useIsFormSubmitDisabled(control)

  const footer = useMemo(() => {
    return (
      <Flex className={footerVariants().buttonContainer()}>
        <Controller
          control={control}
          name="expiration"
          render={({ field }) => (
            <ExpirationSelect
              className="w-full"
              customDate={field.value.customExpiration}
              error={errors.expiration?.customExpiration?.message}
              onChange={value => {
                field.onChange({
                  ...field.value,
                  expirationType: value,
                })
                setListingExpiration(value)
              }}
              onCustomDateChange={date => {
                field.onChange({
                  ...field.value,
                  customExpiration: date,
                })
                setListingExpirationCustom(date.toISOString())
              }}
              value={field.value.expirationType}
            />
          )}
        />

        <Button disabled={isFormSubmitDisabled} type="submit">
          {isEditListing
            ? t("editButton")
            : t("reviewButton", { count: listings.length })}
        </Button>
      </Flex>
    )
  }, [
    control,
    isFormSubmitDisabled,
    isEditListing,
    listings.length,
    errors.expiration,
    setListingExpiration,
    setListingExpirationCustom,
    t,
  ])

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <BottomSheetHeader className="justify-between">
        <BottomSheetHeaderTitle className="whitespace-nowrap">
          {isEditListing
            ? t("editTitle", { count: listings.length })
            : t("createTitle", { count: listings.length })}
        </BottomSheetHeaderTitle>
        <Media greaterThanOrEqual="md">
          <ListingFlowHeaderActions
            listingSymbol={items[0]?.chain.listingCurrency.symbol || "ETH"}
          />
        </Media>
      </BottomSheetHeader>

      <BottomSheetBody className="mt-2 pb-0">
        <Media lessThan="md">
          <ListingFlowHeaderActions
            className="pb-4"
            listingSymbol={items[0]?.chain.listingCurrency.symbol || "ETH"}
          />
        </Media>

        <ListingFlowItemsTable isReview={false} items={items} />

        <Separator className="mt-1" />

        {items[0]?.collection && (
          <CreateOrdersSummary
            adjustForFees={adjustForFees}
            className="md:w-fit"
            collection={items[0].collection}
            creatorFeeEnforced={creatorFeeEnforced}
            currencyIdentifier={{
              contractAddress:
                items[0].collection.chain.listingCurrency.address,
              chainIdentifier: items[0].collection.chain.identifier,
            }}
            orderSide="listing"
            orders={listings}
            symbol={items[0].collection.chain.listingCurrency.symbol}
          />
        )}

        <Media lessThan="md">{footer}</Media>
      </BottomSheetBody>

      <Media greaterThanOrEqual="md">
        <BottomSheetFooter>{footer}</BottomSheetFooter>
      </Media>
    </Form>
  )
}

type ListingFlowFormSkeletonProps = {
  count: number
}

export function ListingFlowFormSkeleton({
  count,
}: ListingFlowFormSkeletonProps) {
  const t = useTranslations("wallet.ListingFlowForm")
  return (
    <>
      <BottomSheetHeader className="justify-between">
        <BottomSheetHeaderTitle>
          {t("createTitle", { count })}
        </BottomSheetHeaderTitle>
        <Media greaterThanOrEqual="md">
          <ListingFlowHeaderActionsSkeleton count={count} />
        </Media>
      </BottomSheetHeader>
      <BottomSheetBody>
        <Media lessThan="md">
          <ListingFlowHeaderActionsSkeleton className="pb-4" count={count} />
        </Media>
        <ListingFlowItemsTableSkeleton count={count} />
        <Separator className="mt-1" />
        <CreateOrdersSummarySkeleton orderSide="listing" />
      </BottomSheetBody>
      <BottomSheetFooter>
        <Flex className={footerVariants().buttonContainer()}>
          <SkeletonBlock className="h-10 w-full rounded md:w-44" />
          <SkeletonBlock className="h-10 w-full rounded md:w-24" />
        </Flex>
      </BottomSheetFooter>
    </>
  )
}
